import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';

import { AuthService, ConfigStateService, DynamicLayoutComponent } from '@abp/ng.core';
import { LoaderBarComponent, PageAlertService } from '@abp/ng.theme.shared';
import { KENDO_DIALOG } from '@progress/kendo-angular-dialog';
import { FooterLink, FooterLinksService, FooterNav } from '@volo/ngx-lepton-x.core';
import { OAuthService } from 'angular-oauth2-oidc';
import { Subject, combineLatest, filter, take, takeUntil } from 'rxjs';

import { ReplaceComponentsService } from '@nexweb/shared/abp/ui';
import { BmSystemMessagingHubService } from '@nexweb/shared/bm-system-messaging-services';
import { FeatureFlagNames } from '@nexweb/shared/guards/feature-flag';
import { LoadingPanelComponent } from '@nexweb/shared/loading';
import { SystemMessagingHubService } from '@nexweb/shared/system-messaging';
import { SessionExpiryDialogComponent } from '@nexweb/shared/ui/user-idle';

@Component({
  selector: 'nexweb-root',
  templateUrl: './app.component.html',
  imports: [
    LoaderBarComponent,
    DynamicLayoutComponent,
    SessionExpiryDialogComponent,
    KENDO_DIALOG,
    LoadingPanelComponent,
  ],
})
export class AppComponent implements OnDestroy, OnInit {
  private authService = inject(AuthService);
  private pageAlertService = inject(PageAlertService);
  private router = inject(Router);
  private oAuthService = inject(OAuthService);
  //  private sessionExpiryService = inject(SessionExpiryService);
  private configStateService = inject(ConfigStateService);
  private systemMessagingHubService = inject(SystemMessagingHubService);
  private bmSystemMessagingHubService = inject(BmSystemMessagingHubService);
  private replaceComponentsService = inject(ReplaceComponentsService);
  private footerLinksService = inject(FooterLinksService);

  private _onDestroy$ = new Subject<void>();

  constructor() {
    // this.initSessionExpiry();
  }

  /*
   * When user closes browser window
   * Remove all browser window local storage data
   */
  // @HostListener('window:unload', ['$event'])
  // unloadHandler(event) {
  //   if (window.onclose) {
  //     this.sessionExpiryService.clearLoginLocalStorage();
  //   }
  // }

  ngOnInit(): void {
    this.clearPageAlertsOnNavigation();
    this.listenForLogout();
    this.initSystemMessagingHub();
    this.initBmSystemMessagingHub();
    this.replaceComponentsService.replaceAbpComponents();
    this.initFooterItems();
  }

  listenForLogout(): void {
    // sometimes the logout event is not triggered by abp auth service
    this.oAuthService.events
      .pipe(filter((e) => e.type === 'logout'))
      .pipe(takeUntil(this._onDestroy$))
      .subscribe(() => {
        this.authService.navigateToLogin();
        this.clearPageAlerts();
      });
  }

  ngOnDestroy(): void {
    // this.sessionExpiryService.onDestroy();
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  //private initSessionExpiry(): void {
  //  const userIdleActive = JSON.parse(this.configStateService.getSetting('NexBase.UserIdle.Enabled'));
  //  if (userIdleActive) {
  //    this.sessionExpiryService.initialize();
  //  }
  //}

  private initSystemMessagingHub(): void {
    this.configStateService
      .getOne$('currentUser')
      .pipe(takeUntil(this._onDestroy$))
      .subscribe((currentUser) => {
        if (currentUser.isAuthenticated) {
          this.systemMessagingHubService.initialize();
        } else {
          this.systemMessagingHubService.stopConnection();
        }
      });
  }

  private initBmSystemMessagingHub(): void {
    combineLatest([
      this.configStateService.getOne$('currentUser'),
      this.configStateService.getFeature$(FeatureFlagNames.balancingMarketEnabled),
    ])
      .pipe(takeUntil(this._onDestroy$))
      .subscribe(([currentUser, featureFlag]) => {
        if (currentUser.isAuthenticated && JSON.parse(featureFlag)) {
          this.bmSystemMessagingHubService.initialize();
        } else {
          this.bmSystemMessagingHubService.stopConnection();
        }
      });
  }

  private initFooterItems(): void {
    const footerLinks = [] as FooterLink[];

    if (window['__env']?.abpVersion) {
      footerLinks.push({ link: '', text: `Abp: ${window['__env']?.abpVersion}` } as FooterLink);
    }

    if (window['__env']?.buildNumber) {
      footerLinks.push({ link: '', text: `Build: ${window['__env']?.buildNumber}` } as FooterLink);
    }

    // Published even when there are no version links: the store starts out holding the Lepton
    // Theme branding, and overwriting it is what removes it.
    const footerInfo: FooterNav = {
      brandName: '',
      brandUrl: '',
      authorName: '',
      authorUrl: '',
      links: footerLinks,
    };

    this.footerLinksService.setFooterInfo(footerInfo);
  }

  private clearPageAlertsOnNavigation(): void {
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationStart),
        takeUntil(this._onDestroy$),
      )
      .subscribe(() => this.clearPageAlerts());
  }

  private clearPageAlerts(): void {
    this.pageAlertService.alerts$
      .pipe(
        take(1),
        filter((alerts) => alerts.length > 0),
      )
      .subscribe((alerts) => {
        alerts.forEach((_, index) => this.pageAlertService.remove(index));
      });
  }
}
